/**
 * Утилиты для хранения согласий на обработку персональных данных (ФЗ-152 РФ)
 * Согласия сохраняются в localStorage для аудита и соответствия требованиям закона.
 */

const STORAGE_KEYS = {
  PD_CONSENT: 'pd_consent',
  COOKIE_CONSENT: 'cookie_consent',
  CONSENT_VERSION: 'consent_version',
  CONSENT_SYNCED_PD: 'consent_synced_pd',
  CONSENT_SYNCED_COOKIE: 'consent_synced_cookie',
} as const;

const CONSENT_VERSION = '1.0';

export interface ConsentRecord {
  accepted: boolean;
  timestamp: string; // ISO 8601
  version: string;
  ip?: string; // Опционально, если бэкенд передаёт
}

/**
 * Сохраняет согласие на обработку персональных данных
 */
export function savePDConsent(accepted: boolean): void {
  const record: ConsentRecord = {
    accepted,
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION,
  };
  try {
    localStorage.setItem(STORAGE_KEYS.PD_CONSENT, JSON.stringify(record));
    localStorage.setItem(STORAGE_KEYS.CONSENT_VERSION, CONSENT_VERSION);
  } catch (e) {
    console.warn('pdConsent: не удалось сохранить согласие', e);
  }
}

/**
 * Проверяет, дано ли согласие на обработку ПДн
 */
export function hasPDConsent(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.PD_CONSENT);
    if (!raw) return false;
    const record: ConsentRecord = JSON.parse(raw);
    return record?.accepted === true;
  } catch {
    return false;
  }
}

/**
 * Сохраняет согласие на использование cookies
 */
export function saveCookieConsent(accepted: boolean): void {
  const record: ConsentRecord = {
    accepted,
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION,
  };
  try {
    localStorage.setItem(STORAGE_KEYS.COOKIE_CONSENT, JSON.stringify(record));
  } catch (e) {
    console.warn('pdConsent: не удалось сохранить cookie consent', e);
  }
}

/**
 * Проверяет, дано ли согласие на cookies
 */
export function hasCookieConsent(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.COOKIE_CONSENT);
    if (!raw) return false;
    const record: ConsentRecord = JSON.parse(raw);
    return record?.accepted === true;
  } catch {
    return false;
  }
}

/**
 * Проверяет, было ли уже показано окно cookie consent (чтобы не показывать повторно при отказе)
 */
export function hasCookieConsentAnswer(): boolean {
  return localStorage.getItem(STORAGE_KEYS.COOKIE_CONSENT) !== null;
}

/**
 * Проверяет, было ли согласие на ПДн уже отправлено на бэкенд
 */
export function isPDConsentSynced(): boolean {
  return localStorage.getItem(STORAGE_KEYS.CONSENT_SYNCED_PD) === '1';
}

/**
 * Проверяет, было ли согласие на cookies уже отправлено на бэкенд
 */
export function isCookieConsentSynced(): boolean {
  return localStorage.getItem(STORAGE_KEYS.CONSENT_SYNCED_COOKIE) === '1';
}

/**
 * Помечает согласие на ПДн как отправленное
 */
export function markPDConsentSynced(): void {
  try {
    localStorage.setItem(STORAGE_KEYS.CONSENT_SYNCED_PD, '1');
  } catch {}
}

/**
 * Помечает согласие на cookies как отправленное
 */
export function markCookieConsentSynced(): void {
  try {
    localStorage.setItem(STORAGE_KEYS.CONSENT_SYNCED_COOKIE, '1');
  } catch {}
}

/**
 * Сбрасывает флаги синхронизации (вызывать при logout)
 */
export function clearConsentSyncFlags(): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.CONSENT_SYNCED_PD);
    localStorage.removeItem(STORAGE_KEYS.CONSENT_SYNCED_COOKIE);
  } catch {}
}

/**
 * Получает сырую запись согласия из localStorage (для отправки на бэкенд)
 */
export function getPDConsentRecord(): ConsentRecord | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.PD_CONSENT);
    if (!raw) return null;
    const record: ConsentRecord = JSON.parse(raw);
    return record?.accepted ? record : null;
  } catch {
    return null;
  }
}

/**
 * Получает сырую запись cookie consent из localStorage (для отправки на бэкенд)
 */
export function getCookieConsentRecord(): ConsentRecord | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.COOKIE_CONSENT);
    if (!raw) return null;
    const record: ConsentRecord = JSON.parse(raw);
    return record?.accepted ? record : null;
  } catch {
    return null;
  }
}

/**
 * Получает запись PD consent для синхронизации на бэкенд (включая rejected).
 * ФЗ-152: фиксируем факт согласия/отказа для аудита.
 */
export function getRawPDConsentRecord(): ConsentRecord | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.PD_CONSENT);
    if (!raw) return null;
    const record: ConsentRecord = JSON.parse(raw);
    return record ?? null;
  } catch {
    return null;
  }
}

/**
 * Получает запись cookie consent для синхронизации на бэкенд (включая rejected).
 */
export function getRawCookieConsentRecord(): ConsentRecord | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.COOKIE_CONSENT);
    if (!raw) return null;
    const record: ConsentRecord = JSON.parse(raw);
    return record ?? null;
  } catch {
    return null;
  }
}
